import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface RemovalResult {
  success: boolean;
  transacao_origem_id: string;
  cartao_id: string;
  faturas_afetadas: Array<{
    mes_referencia: number;
    ano_referencia: number;
    valor_anterior: number;
    valor_parcela: number;
  }>;
  message: string;
}

export const useCreditCardOperations = () => {
  const [isLoading, setIsLoading] = useState(false);

  const removeInstallmentExpense = async (transacaoId: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .rpc('remove_despesa_parcelada', {
          p_transacao_origem_id: transacaoId
        });

      if (error) {
        console.error('Erro ao remover despesa parcelada:', error);
        toast.error('Erro ao remover despesa parcelada: ' + error.message);
        return false;
      }

      const result = data as unknown as RemovalResult;
      
      if (result.success) {
        const faturaCount = result.faturas_afetadas?.length || 0;
        toast.success(
          `Despesa parcelada removida com sucesso! ${faturaCount} fatura(s) foram atualizadas.`,
          { duration: 4000 }
        );
        return true;
      } else {
        toast.error('Erro inesperado ao remover despesa');
        return false;
      }
    } catch (error) {
      console.error('Erro inesperado:', error);
      toast.error('Erro inesperado ao remover despesa');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const confirmAndRemoveInstallment = async (
    transacaoId: string,
    descricao: string,
    valor: number,
    parcelas?: number,
    skipConfirm: boolean = false
  ): Promise<boolean> => {
    const isParcelado = parcelas && parcelas > 1;
    
    if (!skipConfirm) {
      const confirmed = window.confirm(
        isParcelado 
          ? `Tem certeza que deseja excluir permanentemente a despesa parcelada "${descricao}"?\n\n` +
            `• Valor: R$ ${valor.toFixed(2)}\n` +
            `• Parcelas: ${parcelas}x\n\n` +
            `Esta ação irá:\n` +
            `• Excluir permanentemente todas as parcelas do banco de dados\n` +
            `• Recalcular automaticamente as faturas do cartão\n` +
            `• Atualizar o limite disponível\n\n` +
            `Esta operação não pode ser desfeita.`
          : `Tem certeza que deseja excluir permanentemente a transação "${descricao}"?\n\n` +
            `• Valor: R$ ${valor.toFixed(2)}\n\n` +
            `Esta ação irá:\n` +
            `• Excluir permanentemente a transação do banco de dados\n` +
            `• Recalcular automaticamente as faturas do cartão\n` +
            `• Atualizar o limite disponível\n\n` +
            `Esta operação não pode ser desfeita.`
      );

      if (!confirmed) {
        return false;
      }
    }

    return await removeInstallmentExpense(transacaoId);
  };

  return {
    removeInstallmentExpense,
    confirmAndRemoveInstallment,
    isLoading
  };
};